export const DEFAULT_IMAGE_MAX_MB = 5;

let cachedLimitMb = null;
let fetchPromise = null;

export function normalizeImageUploadLimitMb(value, fallback = DEFAULT_IMAGE_MAX_MB) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(50, Math.max(1, Math.round(n)));
}

export function formatImageUploadLimitMb(mb) {
  const n = normalizeImageUploadLimitMb(mb);
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10);
}

export function getCachedImageUploadLimitMb() {
  return cachedLimitMb ?? DEFAULT_IMAGE_MAX_MB;
}

export function setCachedImageUploadLimitMb(mb) {
  cachedLimitMb = normalizeImageUploadLimitMb(mb);
}

export function invalidateImageUploadLimitCache() {
  cachedLimitMb = null;
  fetchPromise = null;
}

export function buildImageTooLargeMessage(limitMb = getCachedImageUploadLimitMb()) {
  const mb = formatImageUploadLimitMb(limitMb);
  return `Image is too large. Maximum allowed size is ${mb}MB.`;
}

export function buildImageUploadTooLargeMessage(limitMb = getCachedImageUploadLimitMb()) {
  const mb = formatImageUploadLimitMb(limitMb);
  return `Upload is too large. Use a smaller image (max ${mb}MB), compress the file, or check Storage Settings for the image size limit.`;
}

export function buildImageUploadNetworkMessage(limitMb = getCachedImageUploadLimitMb()) {
  const mb = formatImageUploadLimitMb(limitMb);
  return `Upload failed. The image may be too large for the server limit (max ${mb}MB). Use a smaller file or check Storage Settings in System Settings.`;
}

export function buildImageUploadTimeoutMessage(limitMb = getCachedImageUploadLimitMb()) {
  const mb = formatImageUploadLimitMb(limitMb);
  return `Upload timed out or the file is too large. Try a smaller image (max ${mb}MB).`;
}

export async function fetchImageUploadLimitMb({ force = false } = {}) {
  if (cachedLimitMb != null && !force) return cachedLimitMb;
  if (fetchPromise && !force) return fetchPromise;

  fetchPromise = import('./api/systemSettings.js')
    .then(({ systemSettingsApi }) => systemSettingsApi.getUploadLimits())
    .then((data) => {
      const mb = normalizeImageUploadLimitMb(data?.image_max_size_mb);
      cachedLimitMb = mb;
      return mb;
    })
    .catch(() => {
      cachedLimitMb = cachedLimitMb ?? DEFAULT_IMAGE_MAX_MB;
      return cachedLimitMb;
    })
    .finally(() => {
      fetchPromise = null;
    });

  return fetchPromise;
}

export async function ensureImageUploadLimitMb() {
  return fetchImageUploadLimitMb();
}
